import Button from "@/common/elements/Button";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { getOptionsFromRefList } from "@/util/common_function";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { Form, Popconfirm } from "antd";
import { labels } from "@/util/label";
import { hasCheckSecurity } from "@/util/common_function";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  SUB_MODULES_LIST_WITH_ID,
  MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { useSelector } from "react-redux";

const PropertyListQuickAddForm = forwardRef(
  ({ onQuickAdd, bookingsFieldsEnabled }, ref) => {
    const [form] = Form.useForm();

    const handleAdd = () => {
      if (onQuickAdd) {
        onQuickAdd();
      }
    };

    const fullSearch = useSelector(state => state.fullSearch);

    useImperativeHandle(ref, () => ({
      getFormValues: () => form.getFieldsValue(),
      resetForm: () => form.resetFields(),
      validateFields: () => form.validateFields(),
    }));

    useEffect(() => {
      form.setFieldValue(["inventoryItems", 0, "storageLocation1Id"], 50500006);
      form.setFieldValue(["inventoryItems", 0, "itemStatus"], "Checked In");
    });

    return (
      <>
        <StyledSectionHeading>
          <span>Property List Quick Add</span>
        </StyledSectionHeading>
        <Form form={form} disabled={!bookingsFieldsEnabled}>
          <StyledGridRow gutter={16}>
            <StyledGridCol lg={6}>
              <FormItem
                name={["inventoryItems", 0, "storageLocation1Id"]}
                label={labels.context}
                hidden
              >
                <input type="hidden" />
              </FormItem>
              <FormItem name={["inventoryItems", 0, "itemStatus"]} hidden>
                <input type="hidden" />
              </FormItem>
              <FormItem
                name={["inventoryItems", 0, "storageLocation2Id"]}
                label="Loc. 1"
                rules={ !fullSearch ? [{ required: true, message: "Loc. 1 is required" }] : []}
              >
                <Select
                  showSearch={true}
                  options={getOptionsFromRefList(
                    "Inmate_Property_Storage_Location_1",
                    false,
                    false,
                    true
                  )}
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={3}>
              <FormItem label={<></>}>
                <Popconfirm
                  title="Suggestion"
                  description="Please, pick the loc 1 for the item?"
                  onConfirm={() => {}}
                  okText="Ok"
                  cancelText={null}
                >
                  <Button
                    type="default"
                    color="default"
                    disabled={!bookingsFieldsEnabled}
                  >
                    Suggest
                  </Button>
                </Popconfirm>
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={5}>
              <FormItem
                name={["inventoryItems", 0, "storageLocation3Id"]}
                label="Loc. 2"
                rules={ !fullSearch ? [{ required: true, message: "Loc. 2 is required" }] : []}
              >
                <Select
                  showSearch={true}
                  options={getOptionsFromRefList(
                    "Inmate_Property_Storage_Location_2",
                    false,
                    false,
                    true
                  )}
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={5}>
              <FormItem
                name="inmatePropertyType"
                label="Property Type"
                rules={ !fullSearch ? [ { required: true, message: "Property Type is required" },] : []}
              >
                <Select
                  showSearch={true}
                  options={getOptionsFromRefList("PropertyType")}
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={4}>
              <FormItem
                name={["inventoryItems", 0, "propertyCommon", 0, "color"]}
                label="Color"
                rules={ !fullSearch ? [{ required: true, message: "Color is required" }] : []}
              >
                <Select
                  showSearch={true}
                  options={getOptionsFromRefList("PropertyColor")}
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={3}>
              <FormItem
                name={["inventoryItems", 0, "propertyCommon", 0, "quantity"]}
                label="Quantity"
                rules={ !fullSearch ? [{ required: true, message: "Quantity is required" }] : []}
              >
                <TextField
                  onKeyDown={(e) => {
                    if (
                      e.key.match(/[^0-9]/) &&
                      e.key !== "Backspace" &&
                      e.key !== "Tab" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={4}>
              <FormItem
                name={["inventoryItems", 0, "propertyCommon", 0, "weapon"]}
                label="Weapon?"
                labelPostion="label-right"
                layout="horizontal"
                paddingTop="24px"
                valuePropName="checked"
                // Weapon is not mandatory, so no required rule here.
              >
                <Checkbox />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem
                name={[
                  "inventoryItems",
                  0,
                  "propertyWithSerialNumber",
                  0,
                  "serialNumber",
                ]}
                label="Serial Number"
                // Serial Number is not mandatory.
              >
                <TextField />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={11}>
              <FormItem
                name={["inventoryItems", 0, "propertyCommon", 0, "description"]}
                label="Description"
                // Description is not mandatory.
              >
                <StyledFlex gap="10px">
                  <TextField />
                  <Button
                    onClick={handleAdd}
                    disabled={
                      !bookingsFieldsEnabled ||
                      !hasCheckSecurity(
                        Security_Types.functionality,
                        Entity_types.subModule,
                        LABELS_CAPITAL.ADD,
                        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                        SUB_MODULES_LIST_WITH_ID.Inmate_Property
                      )
                    }
                  >
                    Q-Add
                  </Button>
                </StyledFlex>
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>
      </>
    );
  }
);
export default PropertyListQuickAddForm;
